import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Box, Typography } from "@mui/material";
import Papa from "papaparse";
import PropTypes from "prop-types";

import { Loader, SvgIcon } from "components";
import { useToaster } from "hooks";

import theme from "themeProvider";
import { error } from "constants";

/**
 *
 * @description CSV/Excel Upload component with max file size check &
 * callback function prop
 */
const UploadCSV = forwardRef(
  (
    {
      headerText,
      parsedFileCallbackFn,
      fileCallBackFn,
      allowedMaxSize = 2,
      fileType = "csv", // string | array
      isLoading = false,
      subHeaderText,
      instructionMsg,
      validateBeforeUpload,
    },
    ref,
  ) => {
    const fileUpload = useRef(null);
    const [file, setFile] = useState();
    const triggerToaster = useToaster();

    // Normalize fileType into array
    const fileTypes = Array.isArray(fileType) ? fileType : [fileType];

    const allowedFileTypes = {
      csv: "text/csv",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      zip: "application/x-zip-compressed",
    };

    /**
     * @description handle click & drag operation
     */
    const handleClick = () => {
      if (!isLoading) {
        // Run custom validation if provided
        if (validateBeforeUpload && !validateBeforeUpload()) {
          return;
        }
        fileUpload.current?.click();
      } else {
        triggerToaster(
          `The upload for the file ${file?.name} is in progress. Two simultaneous file uploads are not allowed.`,
          error,
        );
      }
    };

    const handleDragOver = (e) => e.preventDefault();

    /**
     * @description Read uploaded file & invoke callback Fn
     * @param {*} uploadedFile file contents
     */
    const parseFile = (uploadedFile, type) => {
      try {
        const reader = new FileReader();

        reader.onload = async ({ target }) => {
          if (type === "csv") {
            const csv = Papa.parse(target.result, {
              header: true,
              skipEmptyLines: true,
            });
            const parsedData = csv?.data;
            if (parsedData.length) {
              parsedFileCallbackFn?.(parsedData);
            } else {
              triggerToaster(`No data available`, error);
            }
          } else if (type === "xlsx" || type === "xls") {
            // Excel parsing logic (needs xlsx lib)
            console.log(
              "Parsing logic for Excel (xls/xlsx) needs to be implemented",
            );
          }
        };

        if (type === "csv") {
          reader.readAsText(uploadedFile);
        } else if (type === "xlsx" || type === "xls") {
          reader.readAsArrayBuffer(uploadedFile);
        }
      } catch (ex) {
        console.log(`Exception occurred in reading file ${ex}`);
      }
    };

    /**
     * @description handle all file validations & invoke file parsing
     */
    const handleFileUpload = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const files = [
        ...(e.type === "drop" ? e.dataTransfer.files : e.target.files),
      ];

      if (files.length > 1) {
        triggerToaster(`Only 1 file can be uploaded at a time`, error);
        return;
      }

      const uploadedFile = files?.[0];
      const fileSize = uploadedFile?.size / 1024 / 1024; // in MB

      if (fileSize.toFixed(2) > allowedMaxSize) {
        triggerToaster(`Allowed max file size is ${allowedMaxSize}MB`, error);
        return;
      }

      // Validate file type
      const matchedType = fileTypes.find(
        (type) => uploadedFile?.type === allowedFileTypes[type],
      );

      if (!matchedType) {
        triggerToaster(`Please upload a ${fileTypes.join(" or ")} file`, error);
        return;
      }

      if (uploadedFile) {
        setFile(uploadedFile);
        parseFile(uploadedFile, matchedType);
        // Pass file to callback
        if (fileCallBackFn) {
          fileCallBackFn(uploadedFile);
        }
      }
    };

    // hook to expose function - which will return file and reset file
    useImperativeHandle(ref, () => ({
      getFile: () => file,
      resetFile: () => setFile(null),
    }));

    return (
      <>
        {!!headerText && (
          <Box display="flex" alignItems="center" mb={4}>
            <Typography fontSize={18} fontWeight="bold">
              {headerText}
            </Typography>
            {subHeaderText && (
              <Typography fontSize={14} ml={2} color="text.secondary">
                {subHeaderText}
              </Typography>
            )}
          </Box>
        )}
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          style={{
            border: 1,
            borderStyle: "dashed",
            borderColor: theme.palette.grey[900],
            borderRadius: 12,
            backgroundColor: theme.palette.info.lightBlueBackround,
          }}
          mb={2}
          p={12}
          pt={isLoading ? 0 : 4}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDrop={handleFileUpload}
        >
          {isLoading ? (
            <Box>
              <Loader sx={{ marginBottom: 20 }} />
              <Typography>
                Uploading <b> {file?.name || ""}</b>
              </Typography>
            </Box>
          ) : (
            <>
              {/* OnClick file upload */}
              <input
                style={{ display: "none" }}
                type="file"
                data-testid="upload-input"
                ref={fileUpload}
                onClick={(event) => {
                  event.target.value = null;
                }}
                onChange={handleFileUpload}
              />
              <SvgIcon
                name={"file"}
                style={{
                  height: 48,
                  width: 48,
                  marginBottom: 20,
                }}
              />
              {file && <Typography marginBottom={2}>{file?.name}</Typography>}
              {!file && (
                <>
                  <Typography style={{ fontSize: 16, lineHeight: 1.25 }}>
                    Click or drag file to this area to upload
                  </Typography>
                  <Typography
                    style={{
                      fontSize: 14,
                      color: theme.palette.grey[1000],
                      lineHeight: 1.2,
                    }}
                  >
                    Support for a single or bulk upload. Maximum file size{" "}
                    {allowedMaxSize} MB.
                  </Typography>

                  {instructionMsg && (
                    <Typography
                      style={{
                        fontSize: 14,
                        color: theme.palette.grey["1000"],
                        lineHeight: 1.2,
                      }}
                    >
                      {instructionMsg}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </>
    );
  },
);

UploadCSV.propTypes = {
  headerText: PropTypes.string,
  parsedFileCallbackFn: PropTypes.func,
  fileCallBackFn: PropTypes.func,
  allowedMaxSize: PropTypes.number,
  fileType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  validateBeforeUpload: PropTypes.func,
};

export default UploadCSV;
